// @ts-nocheck
import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import Router, { useRouter } from 'next/router';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import PS from '@/services/permission/permission-service';
import Breadcrumbs from '@/components/breadcrumbs';
import Storage from '@/utils/storage';
import { Box, Card, Flex, Tabs, Tooltip } from "@mantine/core";
import InventoryTable from "@/PageComponents/Table/Component Tables/InventoryTable";
import InventoryCategoriesTable from "@/PageComponents/Table/Component Tables/InventoryCategoriesTable";
import InventorySubcategoriesTable from "@/PageComponents/Table/Component Tables/InventorySubcategoryTable";
import InventorySupplierTable from "@/PageComponents/Table/Component Tables/InventorySupplierTable";
import SCMessageBarContext from '@/utils/contexts/sc-message-bar-context';
import constants from '@/utils/constants';
import tabStyles from '@/styles/Tabs.module.css';
import { useQuery } from "@tanstack/react-query";
import { FieldSetting, getFieldSettings } from "@/PageComponents/Settings/Field Settings/FieldSettings";
import { getSystemNameForFormName } from "@/PageComponents/Settings/Field Settings/fieldSettingHelper";
import BundlesTable from '@/PageComponents/Table/Component Tables/BundlesTable';
import featureService from '@/services/feature/feature-service';
import { IconCrown, IconExternalLink, IconHeartBolt } from "@tabler/icons-react";
import PremiumNewIcon from "@/PageComponents/Premium/PremiumNewIcon";
import NewText from "@/PageComponents/Premium/NewText";
import WarehouseStockTable from '@/PageComponents/Table/Component Tables/WarehouseStockTable';
import StockTransactionTable from '@/PageComponents/Table/Component Tables/StockTransactionTable';
import StockTransactionDetailTable from '@/PageComponents/Table/Component Tables/StockTransactionDetailTable';
import Link from 'next/link';
import StockTakeList from "@/PageComponents/Table/Component Tables/StockTakeList";
import StockTakeStatCards from "@/PageComponents/Stock Take/StockTakeStatCards";
import VanStockList from '@/PageComponents/Table/Component Tables/VanStockList';
import StockTakeTemplateList from '@/PageComponents/Table/Component Tables/StockTakeTemplateList';
import ComingSoonText from '@/PageComponents/Premium/ComingSoonText';
import BetaText from '@/PageComponents/Premium/BetaText';
import { createFileRoute } from '@tanstack/react-router'

function InventoryList() {

    /** fieldSettings Start */
    const inventoryFieldSettings = useQuery(['inventoryFieldSettings'], () => getFieldSettings(Enums.Module.Inventory))
    const settingsBySystemName: { [fieldSystemName: string]: FieldSetting } = useMemo(() => {
        if (inventoryFieldSettings.data) {
            return inventoryFieldSettings.data.reduce((previousValue: any, currentValue: any) => ({
                ...previousValue,
                [currentValue.FieldSystemName]: { ...currentValue }
            }), {})
        } else {
            return {}
        }
    }, [inventoryFieldSettings.data])
    const isShown = useCallback((name: string) => {
        const systemName = getSystemNameForFormName(name)
        return settingsBySystemName.hasOwnProperty(systemName) ? settingsBySystemName[systemName].IsActive : false
    }, [settingsBySystemName])
    /** fieldSettings End */

    const [viewStockTransactionsPermission] = useState(PS.hasPermission(Enums.PermissionName.StockTransactionsView));
    const [viewStocktakePermission] = useState(PS.hasPermission(Enums.PermissionName.StockTake) || PS.hasPermission(Enums.PermissionName.StockTakeManager));
    const [viewVanStockPermission] = useState(PS.hasPermission(Enums.PermissionName.VanManage) || ((Storage.getCookie(Enums.Cookie.hasVan) ?? "false").toLowerCase() === "true" ));

    const messageBarContext = useContext(SCMessageBarContext);

    const router = useRouter()

    const [hasBundles, setHasBundles] = useState<boolean | undefined>();
    const [hasStockControl, setHasStockControl] = useState<boolean | undefined>();
    const [hasStockTake, setHasStockTake] = useState<boolean | undefined>();
    const [hasVanStock, setHasVanStock] = useState<boolean | undefined>();
    // const { data: hasStockTake } = useQuery(['hasStockTake'], () => featureService.getFeature(constants.features.STOCK_TAKE))

    const [activeTab, setActiveTab] = useState<string | null>(null);

    const setInitialTab = (tabDataLocal = tabData) => {
        const tab = router.query.tab as string // TODO(tanstack-router): verify router.query → useParams/useSearch
        if (tab && tabDataLocal[tab] && tabDataLocal[tab].access !== false) {
            setActiveTab(tab)
        }
        else {
            setActiveTab(Object.entries(tabDataLocal).find(([, x]: any) => (x as any).access)?.[0] || 'inventory');
        }
    };

    const tabData = useMemo(() => {
        let tabs: any = {};

        tabs.inventory = {
            label: 'Inventory',
            access: PS.hasPermission(Enums.PermissionName.Inventory)
        };

        if (hasStockControl === true) {
            tabs.warehousestock = {
                label: "Stock",
                access: PS.hasPermission(Enums.PermissionName.Inventory)
            };
            if (viewStockTransactionsPermission) {
                tabs.stocktransaction = {
                    label: "Transactions",
                    access: PS.hasPermission(Enums.PermissionName.StockTransactionsView)
                }
                tabs.stocktransactionDetail = {
                    label: "Transaction Detail",
                    access: PS.hasPermission(Enums.PermissionName.StockTransactionsView)
                }
            }
        }

        tabs.categories = {
            label: 'Categories',
            access: PS.hasPermission(Enums.PermissionName.Inventory),
            disabled: !isShown('InventoryCategory'),
            disabledLabel: 'Enable inventory category from field settings'
        };

        tabs.subcategories = {
            label: 'Subcategories',
            access: PS.hasPermission(Enums.PermissionName.Inventory),
            disabled: !isShown('InventorySubcategory'),
            disabledLabel: 'Enable inventory subcategory from field settings'
        };

        tabs.suppliers = {
            label: 'Suppliers',
            access: PS.hasPermission(Enums.PermissionName.Inventory),
            disabled: !isShown('Supplier'),
            disabledLabel: 'Enable inventory supplier from field settings'
        };

        if (hasBundles === true) {
            tabs.bundles = {
                label: 'Bundles',
                access: PS.hasPermission(Enums.PermissionName.Inventory)
            };
        }

        if (hasStockControl) {
            if (hasStockTake) {
                tabs.stocktake = {
                    label: 'Stock Takes',
                    new: true,
                    access: viewStocktakePermission,
                };
                tabs.sttemplates = {
                    label: 'Stock Take Templates',
                    access: viewStocktakePermission,
                };
                /*tabs.stschedules = {
                    label: 'Stock Take Schedules',
                    new: true,
                    access: false && viewStocktakePermission,
                };*/
            }

            if (hasVanStock) {
                tabs.vanstock = {
                    label: 'Van Stock',
                    beta: true,
                    access: viewVanStockPermission
                };
            }
        }

        setInitialTab(tabs);

        return tabs;
    }, [settingsBySystemName, hasBundles, hasStockControl, hasVanStock, hasStockTake])

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
            Helper.nextRouter(Router.replace, "/");
        }
    }, [accessStatus]);

    const handleTabChange = useCallback((newTabName: string | null) => {
        setActiveTab(newTabName)
        router.replace(router.asPath.split('?')[0] + '?tab=' + newTabName)
    }, [router])

    useEffect(() => {
        getAccessStatus();
        featureService.getFeature(constants.features.INVENTORY_SECTION_BUNDLE).then((feature: any) => {
            setHasBundles(!!feature);
        });
        featureService.getFeature(constants.features.STOCK_CONTROL).then((feature: any) => {
            setHasStockControl(!!feature);
        });
        featureService.getFeature(constants.features.VAN_STOCK).then((feature: any) => {
            setHasVanStock(!!feature);
        });
        featureService.getFeature(constants.features.STOCK_TAKE).then((feature: any) => {
            setHasStockTake(!!feature);
        });
    }, []);

    const tabs = useMemo(() => (
        <>
            <Tabs value={activeTab} onChange={handleTabChange}
                classNames={{
                    tab: tabStyles.scTab,
                    list: tabStyles.scTabList,
                    tabLabel: tabStyles.scTabLabel
                }}
            >
                {(() => {
                    const row1Order = ['inventory', 'bundles', 'categories', 'subcategories', 'suppliers'];
                    const row2Order = ['warehousestock', 'stocktake', 'sttemplates', 'vanstock', 'stocktransaction', 'stocktransactionDetail', 'stschedules'];

                    const isInactive = (d: any) => !d?.access || !!d?.disabled;

                    const orderByRow = (order: string[]) => {
                        const presentKeys = order.filter((k: any) => (tabData as any)[k]);
                        const activeKeys = presentKeys.filter((k: any) => !isInactive((tabData as any)[k]));
                        const inactiveKeys = presentKeys.filter((k: any) => isInactive((tabData as any)[k]));
                        return [...activeKeys, ...inactiveKeys];
                    };

                    const renderTab = (tabName: string, d: any) => (
                        <Tooltip label={d.disabledLabel || ''}
                            key={tabName + 'tab'}
                            position={'bottom'}
                            disabled={!d.disabled || !d.disabledLabel}
                            color={'scBlue'}
                            openDelay={1000}
                            events={{ hover: true, focus: true, touch: true }}
                        >
                            <Tabs.Tab value={tabName}
                                disabled={!d.access || d.disabled}
                                color={(tabName === 'stocktransaction' || tabName === 'stocktransactionDetail' || tabName === 'stocktake') ? 'yellow' : 'scBlue'}
                            >
                                <Flex align={'center'} gap={5}>
                                    {d.label}
                                        {(d.new) && <NewText />}
                                        {/*{(tabName === 'stschedules') && <ComingSoonText />}*/}
                                        {(d.beta) && <BetaText />}
                                </Flex>
                            </Tabs.Tab>
                        </Tooltip>
                    );

                    const row1 = orderByRow(row1Order);
                    const row2 = orderByRow(row2Order);

                    return (
                        <>
                            <Tabs.List mb={0} pb={0}>
                                {row1.map((key: any) => renderTab(key, (tabData as any)[key]))}
                            </Tabs.List>
                            <Tabs.List mb={0} pb={0}>
                                {row2.map((key: any) => renderTab(key, (tabData as any)[key]))}
                            </Tabs.List>
                        </>
                    );
                })()}
            </Tabs>
        </>
    ), [activeTab, tabData, handleTabChange])

    return (
        <>
            <Box
                mt={'var(--mantine-spacing-sm)'}
                px={'var(--mantine-spacing-md)'}
                mb={0}
                pb={0}
                style={(theme: any) => ({ borderBottom: `${theme.colors.gray[2]} 2px solid` })}
            >
                <Box my={'var(--mantine-spacing-md)'}>
                    <Breadcrumbs currPage={{
                        text: tabData[activeTab || '']?.label,
                        link: `/inventory/list?tab=${activeTab || ''}`,
                        type: 'list'
                    }} />
                </Box>
                {tabs}
            </Box>

            {
            }
            <Box
                // ${activeTab === 'stocktake' ? ' - 85px' : ''}
                h={`calc(100vh - 174px${((messageBarContext as any)?.isActive ? ` - ${constants.messageBarMargin}px` : "")})`}
                style={(theme: any) => ({
                    backgroundColor: theme.colors.gray[1]
                })}
                p={'var(--mantine-spacing-xs)'}
                pb={constants.chatWidgetSafeArea}
            >
                <Card
                    style={{
                        backgroundColor: 'white'
                    }}
                    p={'var(--mantine-spacing-md)'}
                    radius={'md'}
                >
                    {
                        activeTab === 'inventory' &&
                        <InventoryTable /> ||
                        activeTab === 'warehousestock' &&
                        <WarehouseStockTable /> ||
                        activeTab === 'stocktransaction' && viewStockTransactionsPermission &&
                        <StockTransactionTable /> ||
                        activeTab === 'stocktransactionDetail' && viewStockTransactionsPermission &&
                        <StockTransactionDetailTable /> ||
                        activeTab === 'categories' &&
                        <InventoryCategoriesTable /> ||
                        activeTab === 'subcategories' &&
                        <InventorySubcategoriesTable /> ||
                        activeTab === 'suppliers' &&
                        <InventorySupplierTable /> ||
                        activeTab === 'bundles' &&
                        <BundlesTable /> ||
                        activeTab === 'stocktake' && viewStocktakePermission &&
                        <StockTakeList /> ||
                        activeTab === 'sttemplates' && viewStocktakePermission &&
                        <StockTakeTemplateList /> ||
                        activeTab === 'stschedules' && viewStocktakePermission &&
                        <StockTakeList /> ||
                        activeTab === 'vanstock' && viewVanStockPermission &&
                        <VanStockList />
                    }
                </Card>

            </Box>

        </>
    )
}

export default InventoryList;

export const Route = createFileRoute('/_authenticated/inventory/list')({ component: InventoryList })
